import React from "react";
import {
  FaShoppingBag,
  FaRegTrashAlt,
  FaRegHeart,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaSyncAlt,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { getProductPath } from "../../../utils/productSlug";

const getCartItemKey = (item) =>
  `${item.product_id}-${item.product_variant_id ?? "base"}-${item.variant_id ?? "base"}`;

const StockBadge = ({ status, message, availableStock, requestedQuantity, isUnavailable = false, isChecking = false }) => {
  if (isChecking) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-slate-50 border border-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">
        <FaSyncAlt className="h-2.5 w-2.5 animate-spin" />
        Checking this address
      </span>
    );
  }
  if (!status && !isUnavailable) return null;
  const upper = String(status).toUpperCase();
  const hasLimitedQuantity = availableStock != null && Number(availableStock) > 0 && Number(availableStock) < Number(requestedQuantity || 1);
  if (!isUnavailable && (
    ["LIMITED_STOCK", "LOW_STOCK", "INSUFFICIENT_STOCK"].includes(upper) || hasLimitedQuantity
  )) {
    const remaining = Number.isFinite(Number(availableStock)) && availableStock != null
      ? ` (${availableStock} left)`
      : "";
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">
        <FaExclamationTriangle className="h-2.5 w-2.5" />
        {`Limited stock${remaining} — continue to checkout`}
      </span>
    );
  }
  if (upper === "IN_STOCK" && !isUnavailable) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">
        <FaCheckCircle className="h-2.5 w-2.5" />
        {message || "In Stock"}
      </span>
    );
  }
  if (upper === "OUT_OF_STOCK") {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-red-50 border border-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-600">
        <FaTimesCircle className="h-2.5 w-2.5" />
        {message || "Out of Stock"}
      </span>
    );
  }
  if (isUnavailable) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-red-50 border border-red-200 px-1.5 py-0.5 text-[10px] font-bold text-red-700">
        <FaTimesCircle className="h-2.5 w-2.5" />
        {message || "Unavailable at this address"}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 border border-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
      <FaExclamationTriangle className="h-2.5 w-2.5" />
      {message || status}
    </span>
  );
};

const CartProductsList = ({
  cart = [],
  cartCount = 0,
  cartLoading = false,
  stockAvailability = {},
  stockCheckLoading = false,
  stockCheckError = null,
  onIncrement,
  onDecrement,
  onRemove,
  onMoveToWishlist,
  onClearCart,
}) => {
  const navigate = useNavigate();

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/60">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-50">
            <FaShoppingBag className="h-4 w-4 text-[#046a38]" />
          </div>
          <h2 className="text-base font-bold text-gray-900">
            Products
          </h2>
          <span className="rounded-full bg-[#046a38] px-2 py-0.5 text-[11px] font-bold text-white">
            {cartCount}
          </span>
        </div>
        {onClearCart && cart.length > 0 && (
          <button
            type="button"
            onClick={onClearCart}
            className="flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-700 transition-colors"
          >
            <FaRegTrashAlt className="h-3 w-3" />
            Clear All
          </button>
        )}
      </div>

      {stockCheckError && cart.length > 0 && (
        <div className="mx-4 mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800" role="status">
          Could not refresh availability for this address. We’ll verify stock again before checkout.
        </div>
      )}
      {Object.values(stockAvailability).some((entry) => entry.is_unavailable) && (
        <div className="mx-4 mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700" role="alert">
          Some products are unavailable for the selected address. Remove or update the marked items to continue.
        </div>
      )}

      {/* List items */}
      <div className="divide-y divide-gray-100 px-4">
        {cart.map((item) => {
          const price = parseFloat(item.price || 0);
          const origPrice = parseFloat(item.originalPrice || price);
          const qty = parseInt(item.quantity || 1, 10);
          const hasDiscount = origPrice > price;
          const discountPercent = hasDiscount
            ? Math.round(((origPrice - price) / origPrice) * 100)
            : 0;
          const lineSavings = hasDiscount ? (origPrice - price) * qty : 0;
          const lineTotal = price * qty;
          const gstPct = parseFloat(item.gst || 0);
          const gstAmt = (lineTotal * gstPct) / 100;
          const totalWithGst = lineTotal + gstAmt;

          const stockKey = `${item.product_id ?? item.id}-${item.product_variant_id ?? item.variant_id ?? "base"}`;
          const checkedAvailability = stockAvailability[stockKey];
          const stockStatus = checkedAvailability?.stock_status ?? item.stock_status ?? null;
          const stockMessage = checkedAvailability?.stock_message ?? item.stock_message ?? null;
          const itemIsOos = item.is_out_of_stock === true ||
            item.is_out_of_stock === 1 ||
            item.is_out_of_stock === "1" ||
            item.is_out_of_stock === "true" ||
            String(stockStatus || "").toUpperCase() === "OUT_OF_STOCK";
          const isOos = checkedAvailability?.is_unavailable ?? (
            itemIsOos
          );
          const isSubEnabled = item.is_subscription_enabled ?? false;

          // Build product path for navigation
          const productPath = getProductPath({
            product_id: item.product_id,
            product_name: item.name,
          });

          return (
            <div
              key={getCartItemKey(item)}
              className={`flex flex-col gap-2 rounded-lg border-b py-4 px-2 ${isOos ? "border-red-200 bg-red-50/70" : "border-gray-100"}`}
            >
              {/* Tier 1: Image · Name · Delete */}
              <div className="flex items-start gap-3">
                {/* Product image — clickable */}
                <button
                  type="button"
                  onClick={() => productPath && navigate(productPath)}
                  className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-gray-50 border border-gray-100 p-1 flex items-center justify-center transition ${
                    productPath ? "cursor-pointer hover:opacity-80 hover:ring-2 hover:ring-emerald-400 active:scale-95" : "cursor-default"
                  }`}
                  title={productPath ? `View ${item.name}` : item.name}
                >
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.name}
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <FaShoppingBag className="h-6 w-6 text-gray-300" />
                  )}
                </button>

                {/* Name + variant */}
                <div className="min-w-0 flex-1">
                  {/* Product name — clickable */}
                  {productPath ? (
                    <button
                      type="button"
                      onClick={() => navigate(productPath)}
                      className="text-xs sm:text-sm font-bold text-gray-900 leading-snug break-words hover:text-emerald-700 hover:underline transition text-left w-full"
                      title={`View ${item.name}`}
                    >
                      {item.name}
                    </button>
                  ) : (
                    <h3 className="text-xs sm:text-sm font-bold text-gray-900 leading-snug break-words">
                      {item.name}
                    </h3>
                  )}

                  {item.variant && (
                    <p className="mt-0.5 text-[11px] text-gray-500 font-medium">
                      {item.variant}
                    </p>
                  )}

                  {/* Badges row */}
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <StockBadge
                      status={stockStatus}
                      message={stockMessage}
                      availableStock={checkedAvailability?.available_stock}
                      requestedQuantity={qty}
                      isUnavailable={isOos}
                      isChecking={stockCheckLoading && !checkedAvailability}
                    />
                    {isSubEnabled && (
                      <span className="inline-flex items-center gap-1 rounded-md bg-purple-50 border border-purple-100 px-1.5 py-0.5 text-[10px] font-bold text-purple-700">
                        <FaSyncAlt className="h-2.5 w-2.5" />
                        Subscribe
                      </span>
                    )}
                  </div>
                </div>

                {/* Delete button */}
                <button
                  type="button"
                  onClick={() => onRemove(item)}
                  disabled={cartLoading}
                  className="shrink-0 text-gray-400 hover:text-red-500 hover:bg-red-50 p-1.5 rounded-lg transition-colors"
                  title="Remove item"
                >
                  <FaRegTrashAlt className="h-4 w-4" />
                </button>
              </div>

              {/* Tier 2: Price · Qty Stepper · Wishlist */}
              {/* pl-[76px] = image(64px) + gap(12px) — aligns with name above */}
              <div className="flex items-center justify-between pl-[76px]">
                {/* Price info */}
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    {hasDiscount && (
                      <span className="text-xs text-gray-400 line-through">
                        ₹{origPrice.toFixed(2)}
                      </span>
                    )}
                    <span className="text-xs sm:text-sm font-bold text-gray-900">
                      ₹{price.toFixed(2)}
                    </span>
                    {discountPercent > 0 && (
                      <span className="text-xs font-bold text-emerald-600">
                        {discountPercent}% OFF
                      </span>
                    )}
                  </div>

                  {/* Per-line totals */}
                  <div className="flex flex-wrap items-center gap-x-2 text-[10px] text-gray-400">
                    <span>
                      Line total:{" "}
                      <span className="font-semibold text-gray-700">₹{lineTotal.toFixed(2)}</span>
                    </span>
                    {gstPct > 0 && (
                      <span>
                        +GST {gstPct}%{" "}
                        <span className="font-semibold text-gray-700">(₹{gstAmt.toFixed(2)})</span>
                      </span>
                    )}
                    {gstPct > 0 && (
                      <span className="text-gray-500">
                        = <span className="font-semibold">₹{totalWithGst.toFixed(2)}</span>
                      </span>
                    )}
                    {lineSavings > 0 && (
                      <span className="text-emerald-600 font-semibold">
                        Save ₹{lineSavings.toFixed(2)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Controls: qty stepper + wishlist */}
                <div className="flex items-center gap-2 shrink-0">
                  {/* Qty stepper */}
                  <div className="flex items-center rounded-lg border border-gray-300 bg-white">
                    <button
                      type="button"
                      onClick={() => onDecrement(item)}
                      className="flex h-7 w-7 items-center justify-center text-gray-600 hover:bg-gray-100 rounded-l-lg transition-colors"
                    >
                      −
                    </button>
                    <span className="w-7 text-center text-xs font-bold text-gray-900">
                      {item.quantity}
                    </span>
                    <button
                      type="button"
                      onClick={() => onIncrement(item)}
                      className="flex h-7 w-7 items-center justify-center text-gray-600 hover:bg-gray-100 rounded-r-lg transition-colors"
                    >
                      +
                    </button>
                  </div>

                  {/* Wishlist */}
                  {onMoveToWishlist && (
                    <button
                      type="button"
                      onClick={() => onMoveToWishlist(item)}
                      disabled={cartLoading}
                      className="flex items-center gap-1.5 text-gray-500 hover:text-rose-500 hover:bg-rose-50 px-2 py-1.5 rounded-lg transition-colors text-xs font-medium border border-gray-200 hover:border-rose-200"
                      title="Move to Wishlist"
                    >
                      <FaRegHeart className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Wishlist</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CartProductsList;
